import { Account } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/i18n/LanguageContext';
import { getAccountTypeColor } from '@/utils/accountUtils';
import {
  Building,
  Building2,
  Coins,
  CreditCard,
  Home,
  MoreHorizontal,
  PiggyBank,
  Wallet,
} from 'lucide-react';

interface AccountCardProps {
  account: Account;
  onViewDetails: (account: Account) => void;
  onEdit: (account: Account) => void;
  onRefreshBalance: (account: Account) => void;
  onDelete: (account: Account) => void;
}

const getAccountIcon = (type: string) => {
  switch (type) {
    case 'bank':
      return <Building className="h-5 w-5" />;
    case 'investment':
      return <PiggyBank className="h-5 w-5" />;
    case 'real-estate':
      return <Home className="h-5 w-5" />;
    case 'crypto':
      return <Coins className="h-5 w-5" />;
    case 'alternative':
      return <Building2 className="h-5 w-5" />;
    case 'liability':
      return <CreditCard className="h-5 w-5" />;
    default:
      return <Wallet className="h-5 w-5" />;
  }
};

export function AccountCard({
  account,
  onViewDetails,
  onEdit,
  onRefreshBalance,
  onDelete,
}: AccountCardProps) {
  const { t } = useLanguage();
  const iconColor = getAccountTypeColor(account.type);

  return (
    <Card
      key={account.id}
      className="cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
      onClick={() => onViewDetails(account)}
    >
      <div className="flex items-center p-4 pb-0">
        <div className="mr-3 rounded p-2" style={{ backgroundColor: `${iconColor}20` }}>
          <div style={{ color: iconColor }}>{getAccountIcon(account.type)}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium" title={account.name}>
            {account.name}
          </div>
          <div className="text-muted-foreground text-xs">{account.institution}</div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking the menu
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                onViewDetails(account);
              }}
            >
              {t('accounts.actions.viewDetails')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                onEdit(account);
              }}
            >
              {t('accounts.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                onRefreshBalance(account);
              }}
            >
              {t('accounts.actions.refreshBalance')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-500"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                onDelete(account);
              }}
            >
              {t('accounts.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CardContent className="p-4 pt-3">
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold">${account.balance.toLocaleString()}</span>
          <span className="text-muted-foreground text-xs">
            {t('accounts.lastUpdated')} {new Date(account.lastUpdated).toLocaleDateString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
